// Raw → golden lineage browser for the SITE profile's Mastery tab, ported from
// the standalone Golden Explorer's site-detail + scores views.
//
// Three stacked reads of the same cluster:
//   1. Field agreement — per identity field, the distinct values the sources
//      reported and how many members backed each one.
//   2. Source records — one column per source feed, side by side, with each
//      member's field values tinted by whether the sources corroborated them.
//   3. Evidence behind the merge — the representative scored pair's signal bars.
import { useMemo } from "react";
import { Tooltip } from "antd";

import InfoTooltip from "@/components/UI/InfoTooltip";

export type LineageMember = {
  id: string;
  source_system: string;
  source_label: string;
  name: string;
  address: string;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
  institution: string | null;
  department: string | null;
  pi_name: string | null;
  site_number: string | null;
  irb_number: string | null;
  external_org_id: string | null;
  trial_id: string | null;
  confidence_pct: number | null;
  confidence_tier: "high" | "medium" | "low";
  address_line1: string | null;
  address_line2: string | null;
  native_source_id: string | null;
  status: string | null;
  latitude: number | null;
  longitude: number | null;
};

export type FieldClusterValue = {
  value: string;
  member_count: number;
  sources: string[];
  share_pct: number;
};

export type FieldCluster = {
  field: string;
  label: string;
  agreement: "unanimous" | "conflict" | "unreported";
  distinct_count: number;
  reported_by: number;
  missing_count: number;
  values: FieldClusterValue[];
};

export type MergeSignal = {
  key: string;
  label: string;
  score: number | null;
};

export type MergeEvidence = {
  norm_site_id_a: string;
  norm_site_id_b: string;
  weighted_score: number | null;
  confidence: number | null;
  confidence_lower: number | null;
  confidence_upper: number | null;
  accept_basket: string | null;
  match_status: string | null;
  blocking_keys: string[];
  signals_present: number | null;
  signals_missing: number | null;
  model_version: string | null;
  signals: MergeSignal[];
  scored_pair_count: number;
};

type ValueState = "agreed" | "corroborated" | "sole" | "missing";

const STATE_STYLES: Record<
  Exclude<ValueState, "missing">,
  { dot: string; tint: string }
> = {
  // every source that reported this field reported this value
  agreed: { dot: "#22C55E", tint: "transparent" },
  // several members share this value, but the field is in conflict overall
  corroborated: { dot: "#F59E0B", tint: "#FFFBEB" },
  // exactly one member reports this value while others disagree
  sole: { dot: "#EF4444", tint: "#FEF2F2" },
};

const AGREEMENT_BADGE: Record<
  FieldCluster["agreement"],
  { label: string; bg: string; text: string }
> = {
  unanimous: { label: "Agreed", bg: "#DCFCE7", text: "#15803D" },
  conflict: { label: "Conflict", bg: "#FEF3C7", text: "#B45309" },
  unreported: { label: "Not reported", bg: "#E8EEF2", text: "#556C75" },
};

const MATCH_STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  auto_accepted: { bg: "#DCFCE7", text: "#15803D" },
  accepted: { bg: "#DCFCE7", text: "#15803D" },
  review: { bg: "#FEF3C7", text: "#B45309" },
  rejected: { bg: "#FEE2E2", text: "#BE0303" },
};

/** Case- and whitespace-insensitive key, mirroring the backend's clustering. */
function clusterKey(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

function scoreColor(score: number | null): string {
  if (score == null) return "#D5DDE1";
  if (score >= 0.8) return "#22C55E";
  if (score >= 0.5) return "#F59E0B";
  if (score > 0) return "#F97316";
  return "#D5DDE1";
}

function confidenceColor(pct: number | null): string {
  if (pct == null) return "#8C8C8C";
  if (pct >= 90) return "#22C55E";
  if (pct >= 70) return "#F59E0B";
  return "#EF4444";
}

type FieldLookup = Map<
  string,
  {
    agreement: FieldCluster["agreement"];
    values: Map<string, FieldClusterValue>;
  }
>;

function buildLookup(clusters: FieldCluster[]): FieldLookup {
  const lookup: FieldLookup = new Map();
  clusters.forEach((cluster) => {
    lookup.set(cluster.field, {
      agreement: cluster.agreement,
      values: new Map(cluster.values.map((v) => [clusterKey(v.value), v])),
    });
  });
  return lookup;
}

function valueState(
  lookup: FieldLookup,
  field: string,
  raw: string | null | undefined,
): ValueState {
  if (!raw) return "missing";
  const cluster = lookup.get(field);
  if (!cluster) return "missing";
  if (cluster.agreement === "unanimous") return "agreed";
  const match = cluster.values.get(clusterKey(raw));
  if (!match) return "missing";
  return match.member_count > 1 || match.sources.length > 1
    ? "corroborated"
    : "sole";
}

/** One labelled field inside a member card, tinted by cross-source agreement. */
function MemberField({
  label,
  value,
  state,
}: {
  label: string;
  value: string | null;
  state: ValueState;
}) {
  if (!value) return null;
  const style = state === "missing" ? null : STATE_STYLES[state];
  return (
    <div
      className="flex items-start gap-1.5 rounded px-1 py-0.5"
      style={{ backgroundColor: style?.tint ?? "transparent" }}
    >
      <span
        className="mt-[5px] h-1.5 w-1.5 shrink-0 rounded-full"
        style={{ backgroundColor: style?.dot ?? "#D5DDE1" }}
      />
      <div className="flex min-w-0 flex-col">
        <span className="text-[9px] font-semibold uppercase tracking-wide text-[#8C8C8C]">
          {label}
        </span>
        <span className="break-words text-[12px] leading-snug text-[#1F2933]">
          {value}
        </span>
      </div>
    </div>
  );
}

function FieldAgreementRow({ cluster }: { cluster: FieldCluster }) {
  const badge = AGREEMENT_BADGE[cluster.agreement];
  return (
    <div className="flex flex-col gap-1.5 border-t border-[#F0F2F4] py-2.5 first:border-t-0 md:flex-row md:items-start md:gap-4">
      <div className="flex shrink-0 items-center gap-2 md:w-[220px]">
        <span className="text-[12px] font-semibold text-[#2B4752]">
          {cluster.label}
        </span>
        <span
          className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
          style={{ backgroundColor: badge.bg, color: badge.text }}
        >
          {badge.label}
        </span>
        <Tooltip
          title={`${cluster.distinct_count} distinct value${cluster.distinct_count === 1 ? "" : "s"} across ${cluster.reported_by} member record${cluster.reported_by === 1 ? "" : "s"}${cluster.missing_count ? `; ${cluster.missing_count} member${cluster.missing_count === 1 ? "" : "s"} left this field blank` : ""}.`}
        >
          <span className="cursor-help text-[11px] font-semibold text-[#2B4752]">
            {cluster.distinct_count}
          </span>
        </Tooltip>
      </div>
      <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
        {cluster.values.length === 0 ? (
          <span className="text-[12px] text-[#8C8C8C]">
            No source reported this field.
          </span>
        ) : (
          cluster.values.map((v) => (
            <Tooltip
              key={v.value}
              title={`${v.member_count} of ${cluster.reported_by} member record${cluster.reported_by === 1 ? "" : "s"} (${v.share_pct}%) — ${v.sources.join(", ")}`}
            >
              <span
                className="inline-flex max-w-full cursor-help items-center gap-1.5 rounded border px-2 py-1"
                style={{
                  borderColor:
                    cluster.agreement === "unanimous" ? "#BBF7D0" : "#FDE68A",
                  backgroundColor:
                    cluster.agreement === "unanimous" ? "#F0FDF4" : "#FFFBEB",
                }}
              >
                <span className="truncate text-[12px] text-[#1F2933]">
                  {v.value}
                </span>
                <span className="shrink-0 text-[11px] font-semibold text-[#2B4752]">
                  ×{v.member_count}
                </span>
              </span>
            </Tooltip>
          ))
        )}
      </div>
    </div>
  );
}

function MemberCard({
  member,
  lookup,
}: {
  member: LineageMember;
  lookup: FieldLookup;
}) {
  const meta = [
    member.site_number ? `Site #${member.site_number}` : null,
    member.native_source_id ? `Native ${member.native_source_id}` : null,
    member.status,
    member.trial_id,
  ].filter(Boolean) as string[];

  return (
    <div className="flex flex-col gap-1 rounded-[6px] border border-[#F0F2F4] bg-white p-2">
      <div className="flex items-start justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-[#8C8C8C]">
          {member.id}
        </span>
        {member.confidence_pct != null && (
          <Tooltip title="Blended record confidence (name 60% + address completeness 40%) from the normalized-site row.">
            <span
              className="shrink-0 cursor-help text-[11px] font-semibold"
              style={{ color: confidenceColor(member.confidence_pct) }}
            >
              {member.confidence_pct}%
            </span>
          </Tooltip>
        )}
      </div>

      <MemberField
        label="Name"
        value={member.name || null}
        state={valueState(lookup, "name", member.name)}
      />
      <MemberField
        label="Institution"
        value={member.institution}
        state={valueState(lookup, "institution", member.institution)}
      />
      <MemberField
        label="Address"
        value={member.address || null}
        state={valueState(lookup, "address", member.address)}
      />
      <MemberField
        label="Phone"
        value={member.phone}
        state={valueState(lookup, "phone", member.phone)}
      />
      <MemberField
        label="PI"
        value={member.pi_name}
        state={valueState(lookup, "pi_name", member.pi_name)}
      />

      {meta.length > 0 && (
        <div className="flex flex-wrap gap-1 border-t border-[#F5F7F8] pt-1.5">
          {meta.map((m) => (
            <span
              key={m}
              className="rounded bg-[#F3F6F8] px-1.5 py-0.5 text-[10px] text-[#2B4752]"
            >
              {m}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function SignalBar({ signal }: { signal: MergeSignal }) {
  return (
    <div className="grid grid-cols-[132px_1fr_38px] items-center gap-2">
      <span className="truncate text-[11px] text-[#2B4752]">
        {signal.label}
      </span>
      <div className="h-[6px] overflow-hidden rounded-full bg-[#EEF2F4]">
        <div
          className="h-full rounded-full"
          style={{
            width: `${Math.max(0, Math.min(1, signal.score ?? 0)) * 100}%`,
            backgroundColor: scoreColor(signal.score),
          }}
        />
      </div>
      <span className="text-right text-[11px] font-semibold text-[#1F2933]">
        {signal.score == null ? "—" : signal.score.toFixed(2)}
      </span>
    </div>
  );
}

function EvidenceStat({
  label,
  tooltip,
  children,
}: {
  label: string;
  tooltip: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-[#8C8C8C]">
        {label}
        <InfoTooltip title={tooltip} iconSize={10} fill="#9CA3AF" />
      </div>
      <div className="text-[13px] font-semibold text-[#1F2933]">{children}</div>
    </div>
  );
}

/** Which part to render. The MasteryTab shows one per sub-nav item so the
 *  page doesn't become a single unscrollable column; "all" keeps the stacked
 *  layout for any caller that wants the whole story at once. */
export type LineageSectionKey = "agreement" | "records" | "evidence" | "all";

export default function SiteLineageExplorer({
  lineage,
  fieldClusters,
  mergeEvidence,
  section = "all",
}: {
  lineage: LineageMember[];
  fieldClusters: FieldCluster[];
  mergeEvidence: MergeEvidence | null;
  section?: LineageSectionKey;
}) {
  const show = (key: Exclude<LineageSectionKey, "all">) =>
    section === "all" || section === key;
  const lookup = useMemo(() => buildLookup(fieldClusters), [fieldClusters]);

  const bySource = useMemo(() => {
    const groups = new Map<string, LineageMember[]>();
    lineage.forEach((member) => {
      const key = member.source_label || member.source_system || "Unattributed";
      const bucket = groups.get(key);
      if (bucket) bucket.push(member);
      else groups.set(key, [member]);
    });
    return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [lineage]);

  if (lineage.length === 0) return null;

  const conflictCount = fieldClusters.filter(
    (c) => c.agreement === "conflict",
  ).length;
  const matchColors = mergeEvidence?.match_status
    ? (MATCH_STATUS_COLORS[mergeEvidence.match_status] ?? {
        bg: "#E8EEF2",
        text: "#556C75",
      })
    : null;

  return (
    <div className="flex flex-col gap-4">
      {show("agreement") && (
        <div className="overflow-hidden rounded-[8px] border border-[#F0F2F4] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
          <div className="flex items-center gap-2 border-b border-[#F0F2F4] px-4 py-3">
            <span className="font-mcqueenGrotesk text-[14px] font-bold text-[#2B4752]">
              Field agreement across sources
            </span>
            <InfoTooltip
              title="For each identity field, the distinct values the source systems reported for this site. A field is Agreed when every source that reported it reported the same value, and a Conflict when they disagree. Counts show how many raw member records backed each value."
              iconSize={12}
              fill="#9CA3AF"
            />
            {conflictCount > 0 && (
              <span className="ml-auto rounded bg-[#FEF3C7] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#B45309]">
                {conflictCount} field{conflictCount === 1 ? "" : "s"} in
                conflict
              </span>
            )}
          </div>
          <div className="px-4 py-1">
            {fieldClusters.map((cluster) => (
              <FieldAgreementRow key={cluster.field} cluster={cluster} />
            ))}
          </div>
        </div>
      )}

      {show("records") && (
        <div className="overflow-hidden rounded-[8px] border border-[#F0F2F4] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
          <div className="flex items-center gap-2 border-b border-[#F0F2F4] px-4 py-3">
            <span className="font-mcqueenGrotesk text-[14px] font-bold text-[#2B4752]">
              Source records
            </span>
            <InfoTooltip
              title="Every raw record the mastering pipeline folded into this golden site, grouped by the source system it came from. A green dot means all sources agreed on that value, amber means several members share it while the field is contested, and red means only this record reports it."
              iconSize={12}
              fill="#9CA3AF"
            />
            <span className="ml-auto text-[11px] font-semibold text-[#2B4752]">
              {lineage.length} record{lineage.length === 1 ? "" : "s"} ·{" "}
              {bySource.length} source{bySource.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="flex gap-3 overflow-x-auto p-4">
            {bySource.map(([source, members]) => (
              <div
                key={source}
                className="flex w-[280px] shrink-0 flex-col gap-2 rounded-[8px] bg-[#F8FAFB] p-2"
              >
                <div className="flex items-baseline justify-between gap-2 px-1">
                  <span className="truncate font-mcqueenGrotesk text-[12px] font-bold text-[#2B4752]">
                    {source}
                  </span>
                  <span className="shrink-0 text-[11px] font-semibold text-[#2B4752]">
                    {members.length}
                  </span>
                </div>
                {members.map((member) => (
                  <MemberCard key={member.id} member={member} lookup={lookup} />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {show("evidence") && (
        <div className="overflow-hidden rounded-[8px] border border-[#F0F2F4] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
          <div className="flex items-center gap-2 border-b border-[#F0F2F4] px-4 py-3">
            <span className="font-mcqueenGrotesk text-[14px] font-bold text-[#2B4752]">
              Evidence behind the merge
            </span>
            <InfoTooltip
              title="The representative scored pair the mastering model produced for this cluster — the within-cluster pair carrying the most present signals. Each signal is scored 0–1; an em dash means the signal could not be evaluated for this pair rather than that it scored zero."
              iconSize={12}
              fill="#9CA3AF"
            />
            {mergeEvidence && mergeEvidence.scored_pair_count > 1 && (
              <span className="ml-auto text-[11px] font-semibold text-[#2B4752]">
                {mergeEvidence.scored_pair_count} scored pairs in cluster
              </span>
            )}
          </div>

          {!mergeEvidence ? (
            <div className="flex items-center gap-2 px-4 py-6 text-[12px] text-[#4B5563]">
              No scored pairs for this cluster — the golden record was admitted
              by rule rather than by pairwise scoring.
              <InfoTooltip
                title="Seed and rule-admitted golden records skip pairwise scoring by design, so there is no signal evidence to show. This is expected, not a data gap."
                iconSize={11}
                fill="#9CA3AF"
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 p-4 lg:grid-cols-[240px_1fr]">
              <div className="flex flex-col gap-3">
                <EvidenceStat
                  label="Weighted score"
                  tooltip="The model's composite similarity score for this pair, blending every signal by its learned weight."
                >
                  {mergeEvidence.weighted_score != null
                    ? mergeEvidence.weighted_score.toFixed(3)
                    : "—"}
                </EvidenceStat>
                <EvidenceStat
                  label="Confidence"
                  tooltip="Calibrated probability that the two records refer to the same real-world site, with the model's lower and upper bounds."
                >
                  <span
                    style={{
                      color: confidenceColor(
                        mergeEvidence.confidence != null
                          ? mergeEvidence.confidence * 100
                          : null,
                      ),
                    }}
                  >
                    {mergeEvidence.confidence != null
                      ? `${Math.round(mergeEvidence.confidence * 100)}%`
                      : "—"}
                  </span>
                  {mergeEvidence.confidence_lower != null &&
                    mergeEvidence.confidence_upper != null && (
                      <span className="ml-1.5 text-[11px] font-medium text-[#2B4752]">
                        {Math.round(mergeEvidence.confidence_lower * 100)}–
                        {Math.round(mergeEvidence.confidence_upper * 100)}%
                      </span>
                    )}
                </EvidenceStat>
                <EvidenceStat
                  label="Match status"
                  tooltip="The decision the pipeline recorded for this pair — auto-accepted pairs merged without steward review."
                >
                  {matchColors ? (
                    <span
                      className="inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
                      style={{
                        backgroundColor: matchColors.bg,
                        color: matchColors.text,
                      }}
                    >
                      {mergeEvidence.match_status?.replace(/_/g, " ")}
                    </span>
                  ) : (
                    "—"
                  )}
                </EvidenceStat>
                <EvidenceStat
                  label="Accept basket"
                  tooltip="Which acceptance rule the pair cleared — e.g. an identifier-plus-address basket, or a full-signal basket."
                >
                  {mergeEvidence.accept_basket ?? "—"}
                </EvidenceStat>
                {mergeEvidence.blocking_keys.length > 0 && (
                  <EvidenceStat
                    label="Blocking keys"
                    tooltip="The candidate-generation keys that brought these two records into comparison in the first place."
                  >
                    <div className="flex flex-wrap gap-1">
                      {mergeEvidence.blocking_keys.map((key) => (
                        <span
                          key={key}
                          className="rounded bg-[#EEF4F8] px-1.5 py-0.5 text-[11px] font-medium text-[#2B5A6E]"
                        >
                          {key}
                        </span>
                      ))}
                    </div>
                  </EvidenceStat>
                )}
              </div>

              <div>
                <div className="grid grid-cols-1 gap-x-8 gap-y-2 xl:grid-cols-2">
                  {mergeEvidence.signals.map((signal) => (
                    <SignalBar key={signal.key} signal={signal} />
                  ))}
                </div>
                {mergeEvidence.signals_present != null && (
                  <div className="mt-3 flex items-center gap-1 border-t border-[#F0F2F4] pt-2 text-[11px] font-semibold text-[#2B4752]">
                    {mergeEvidence.signals_present} of{" "}
                    {mergeEvidence.signals.length} signals present
                    <InfoTooltip
                      title="A signal is present when both records carried enough data to evaluate it. Missing signals neither help nor hurt the score."
                      iconSize={10}
                      fill="#9CA3AF"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
